import React from 'react';
import Card from '../design-system/Card';
import { colors, spacing, radius, fontStack } from '../design-system/tokens';

/**
 * ProgressCard — icon + label + X / Y + progress fill + primary action.
 *
 * Props:
 *   - icon       : ReactNode  (small SVG, ~22px)
 *   - label      : string     (e.g. "Water")
 *   - value      : number|string  (big number, e.g. 850)
 *   - unit       : string?    (e.g. "ml", "min", "of 3")
 *   - goal       : number?    (denominator for the bar)
 *   - actual     : number?    (numerator; if not provided, falls back to value)
 *   - accent     : string     (color used for the bar fill — e.g. green)
 *   - cta        : { label, onClick }?
 *   - compact    : boolean?
 */
export default function ProgressCard({
  icon,
  label,
  value,
  unit,
  goal,
  actual,
  accent = colors.accentEnergy,
  cta,
  compact = false,
}) {
  const num = typeof actual === 'number' ? actual : Number(value) || 0;
  const pct = goal && goal > 0 ? Math.min(100, Math.round((num / goal) * 100)) : 0;

  return (
    <Card padding={compact ? spacing.base : spacing.lg}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontFamily: fontStack }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {icon && (
            <span
              aria-hidden="true"
              style={{
                width: 32,
                height: 32,
                borderRadius: radius.sm,
                backgroundColor: hexAlpha(accent, 0.14),
                color: accent,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {icon}
            </span>
          )}
          <div
            style={{
              fontSize: 12,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: colors.textSecondary,
              fontWeight: 600,
            }}
          >
            {label}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span
            style={{
              fontSize: compact ? 24 : 32,
              fontWeight: 600,
              color: colors.textPrimary,
              letterSpacing: '-0.02em',
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value}
          </span>
          {unit && (
            <span style={{ fontSize: 13, color: colors.textSecondary }}>{unit}</span>
          )}
        </div>

        {/* Progress bar */}
        <div
          aria-hidden="true"
          style={{
            position: 'relative',
            height: 6,
            borderRadius: 999,
            backgroundColor: 'var(--bg-hover)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              width: `${pct}%`,
              backgroundColor: accent,
              borderRadius: 999,
              transition: 'width 240ms ease',
            }}
          />
        </div>

        {cta && (
          <button
            type="button"
            onClick={cta.onClick}
            style={{
              alignSelf: 'flex-start',
              marginTop: 4,
              padding: '8px 14px',
              minHeight: 36,
              borderRadius: 999,
              border: 'none',
              background: colors.primary,
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: fontStack,
              cursor: 'pointer',
            }}
          >
            {cta.label}
          </button>
        )}
      </div>
    </Card>
  );
}

function hexAlpha(hex, alpha) {
  // Phase 12: accept theme `var(--xxx)` tokens by routing through
  // color-mix (Chromium 111+, Safari 16.2+, FF 113+).
  if (typeof hex === 'string' && hex.startsWith('var(')) {
    const pct = Math.round(alpha * 100);
    return `color-mix(in srgb, ${hex} ${pct}%, transparent)`;
  }
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1].slice(0, 2), 16);
  const g = parseInt(m[1].slice(2, 4), 16);
  const b = parseInt(m[1].slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
